/**
 * Submission-payload shaping for the sales-order form.
 *
 * Turns the Formik form values plus the two picked contacts (shipping and
 * billing) into the request body the create/patch endpoints expect: when
 * "same as shipping" is checked the shipping address is mirrored into the
 * billing fields, each contact resolves to its foreign-key id (or null for a
 * synthetic/manual-entry address), and blank line items are dropped while
 * radio ICCIDs are trimmed to real values on non-draft orders. Returns a new
 * object — the input values are never mutated.
 *
 * Contains:
 * - buildOrderSalePayload — pure values + contacts -> request body.
 * - copyShippingToBillingFields — the single ship->bill field mirror.
 */

// Billing field <- shipping field pairs mirrored when "same as shipping" is on.
const SHIP_TO_BILL_FIELDS = [
  ['bill_contact', 'ship_contact'],
  ['bill_company_name', 'ship_company_name'],
  ['bill_email', 'ship_email'],
  ['bill_phone', 'ship_phone'],
  ['bill_address', 'ship_address'],
  ['bill_city', 'ship_city'],
  ['bill_state', 'ship_state'],
  ['bill_zip', 'ship_zip'],
  ['bill_country', 'ship_country'],
];

/**
 * @param {object} values - Formik form values.
 * @returns {object} The billing fields copied from their shipping counterparts.
 */
function copyShippingToBillingFields(values) {
  const copied = {};
  for (const [billField, shipField] of SHIP_TO_BILL_FIELDS) {
    copied[billField] = values[shipField];
  }
  return copied;
}

// A picked contact contributes its id as an FK only when it is a real saved
// contact; a synthetic/manual-entry address carries no contact FK.
const resolveContactFk = contact => (contact?.id && !contact?._isSynthetic && !contact?._isAddress ? contact.id : null);

/**
 * @param {object}  values                         - Formik form values.
 * @param {object}  contacts
 * @param {object?} contacts.selectedShippingContact - Picked shipping contact, or null.
 * @param {object?} contacts.selectedBillingContact  - Picked billing contact, or null.
 * @returns {object} A new request body for the order-sale create/patch endpoints.
 */
export function buildOrderSalePayload(values, { selectedShippingContact, selectedBillingContact }) {
  const payload = { ...values };

  if (values.billing_same_as_shipping) {
    Object.assign(payload, copyShippingToBillingFields(values));
  }

  payload.shipping_contact = resolveContactFk(selectedShippingContact);

  const effectiveBillContact = values.billing_same_as_shipping ? selectedShippingContact : selectedBillingContact;
  payload.billing_contact = resolveContactFk(effectiveBillContact);

  if (values.order_sale_items.length) {
    payload.order_sale_items = values.order_sale_items
      .filter(item => Boolean(item.item))
      .map(item => ({
        ...item,
        ship_type: values.ship_type === '' ? null : values.ship_type,
        iccids:
          item.has_radios && values.status !== 'Draft' ? item.iccids.filter(i => i.iccid != '' && i.iccid != null) : [],
      }));
  }

  return payload;
}
